import React from "react";
import { Poll } from "@/types/poll";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronLeft, Globe, Hash } from "lucide-react";
import { useCountry } from "@/contexts/country";
import { useCategories } from "@/hooks/useCategories";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import SocialShare from "@/components/social/SocialShare";

interface PollPageHeaderProps {
  poll: Poll;
}

const PollPageHeader: React.FC<PollPageHeaderProps> = ({ poll }) => {
  const { availableCountries } = useCountry();
  const { categories } = useCategories();

  const getCountryName = (code: string | null) => {
    if (!code) return "Global";
    const country = availableCountries.find((c) => c.code === code);
    return country ? country.name : code;
  };

  const getCategoryName = (categoryId: string | undefined) => {
    if (!categoryId) return "General";
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : "General";
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return format(new Date(dateString), "MMM d, yyyy");
  };

  // Prepare share URL and content
  const shareUrl = window.location.href;
  const shareTitle = `Poll: ${poll.title || "Participate in this poll"}`;
  const shareDescription =
    poll.description || "Share your opinion in this poll";

  return (
    <div className="mb-4 w-full overflow-x-hidden">
      {/* Navigation */}
      <div className="mb-4 flex-1 pt-[20px]">
        <div className="hidden sm:block">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to="/"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Home
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to="/polls"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Polls
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {poll.country_code && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/cards/${poll.country_code}`}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {getCountryName(poll.country_code)}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <Link
          to="/polls"
          className="mt-2 inline-flex items-center text-muted-foreground hover:text-foreground transition-colors text-sm"
        ></Link>
      </div>

      {/* Poll Image (if available) */}
      {poll.image_url && (
        <div className="mb-3 w-full">
          <AspectRatio ratio={3 / 2} className="overflow-hidden rounded-lg">
            <img
              src={poll.image_url}
              alt={poll.title}
              className="rounded-lg object-cover w-full h-full shadow-md"
            />
          </AspectRatio>
        </div>
      )}

      <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 overflow-x-auto">
        {poll.poll_number && (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 font-mono text-xs whitespace-nowrap flex-shrink-0"
          >
            <Hash className="h-3 w-3 mr-1" />
            <span>{poll.poll_number}</span>
          </Badge>
        )}

        {poll.status === "live" ? (
          <Badge className="bg-green-500 text-xs whitespace-nowrap">
            Active
          </Badge>
        ) : poll.status === "closed" ? (
          <Badge className="bg-gray-500 text-xs whitespace-nowrap">
            Closed
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs whitespace-nowrap">
            Draft
          </Badge>
        )}

        <Badge variant="secondary" className="text-xs whitespace-nowrap">
          {getCategoryName(poll.category_id)}
        </Badge>

        <Badge
          variant="outline"
          className="flex items-center text-xs whitespace-nowrap"
        >
          <Globe className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>{getCountryName(poll.country_code)}</span>
        </Badge>

        {poll.closes_at && (
          <Badge
            variant="outline"
            className="flex items-center text-xs whitespace-nowrap"
          >
            <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="hidden sm:inline">Closes: </span>
            <span>{formatDate(poll.closes_at)}</span>
          </Badge>
        )}
      </div>

      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-nation-blue dark:text-blue-300 break-words hyphens-auto">
        {poll.title}
      </h1>

      <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base md:text-lg break-words">
        {poll.description}
      </p>

      {/* Social Share buttons */}
      <div className="flex gap-2 mt-4 overflow-x-auto">
        <SocialShare
          url={shareUrl}
          title={shareTitle}
          description={shareDescription}
        />
      </div>
    </div>
  );
};

export default PollPageHeader;
